import { clsx } from "clsx/lite";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronRight } from "@fortawesome/pro-solid-svg-icons";
import { Dispatch, SetStateAction } from "react";
import {
  faArrowTurnLeft,
  faComment,
  faReceipt,
} from "@fortawesome/pro-regular-svg-icons";
import {
  makeCellId,
  onMouseEnterCell,
  onMouseLeaveCell,
  useEmployeeAttendanceRow,
} from "./employeeAttendanceRow/useEmployeeAttendanceRow";
import { TempAttendance } from "@/config/types";
import { AttendanceEmployee, AttendanceMonth } from "@/api/openapi";
// eslint-disable-next-line max-len
import { EmployeeAttendanceActionButton } from "./employeeAttendanceRow/employeeAttendanceActionButton";
// eslint-disable-next-line max-len
import { AttendanceDesktopGiustificativoRow } from "./employeeAttendanceRow/attendanceDesktopGiustificativoRow";
// eslint-disable-next-line max-len
import { AttendanceDesktopAddGiustificativoRow } from "./employeeAttendanceRow/attendanceDesktopAddGiustificativoRow";
import ConfirmationDialog from "@/components/confirmationDialog/ConfirmationDialog";
import { showTranslated, translate } from "@/config/translator";
import {
  formatHours,
  parseHours,
  POSITIVE_GIUSTIFICATIVI,
} from "../../attendanceUtils";

type EmployeeAttendanceRowProps = {
  isLastRow: boolean;
  employeeAttendance: TempAttendance;
  index: number;
  setExpandedEmployee: Dispatch<SetStateAction<AttendanceEmployee | null>>;
  onNoteOpen: () => void;
  onVociOpen: () => void;
  onSaveEdit: (isDraft: boolean, attendances?: AttendanceMonth) => void;
  openRestoreEmployeeDialog: (employee: AttendanceEmployee) => void;
  isSaving: boolean;
  isExpanded: boolean;
  expandedEmployeeIndex: number | null;
};

export function EmployeeAttendanceRow({
  setExpandedEmployee,
  isLastRow,
  employeeAttendance,
  index: employeeIndex,
  onNoteOpen,
  onVociOpen,
  onSaveEdit,
  openRestoreEmployeeDialog,
  isSaving,
  isExpanded,
  expandedEmployeeIndex,
}: EmployeeAttendanceRowProps) {
  const {
    hasRoundedBottom,
    totalOreBancaOre,
    totalHours,
    setCurrentEditingValue,
    onBlurGiustificativoInput,
    daysChanged,
    giustificativi,
    totalActualHours,
    onBlurRegularHoursInput,
    setFocusedDay,
    currentScratchEditingValue,
    setCurrentScratchEditingValue,
    focusedDay,
    isSubmitted,
    collapsibleCellClasses,
    onChangeGiustificativoSearchQuery,
    giustificativoSearchQuery,
    giustificativiSearchResults,
    onAddGiustificativo,
    addGiustificativoPopoverOpen,
    setAddGiustificativoPopoverOpen,
    openRemoveGiustificativoConfirmation,
    closeRemoveGiustificativoConfirmation,
    removeGiustificativoConfirmationOpen,
    wantsToRemoveGiustificativo,
    lang,
    onRemoveGiustificativo,
    attendanceMonth,
  } = useEmployeeAttendanceRow({
    employeeAttendance,
    isExpanded,
    isLastRow,
    onSaveEdit,
  });

  return (
    <>
      {/* EMPLOYEE ROW */}
      <tr className="h-12">
        {/* ID (sticky left) */}
        <td
          className={clsx(
            "sticky",
            "left-0",
            "z-10",
            "min-w-14",
            "text-center",
            "border-b",
            "border-l",
            isExpanded ? "bg-primary-50" : "bg-white",
            "border-neutral",
            "border-r",
            isExpanded && "font-medium",
            hasRoundedBottom && "rounded-bl-xl",
          )}
          id={makeCellId(employeeIndex, -2, null)}
          onMouseEnter={() =>
            attendanceMonth
              ? onMouseEnterCell(
                  employeeIndex,
                  -2,
                  null,
                  expandedEmployeeIndex,
                  expandedEmployeeIndex
                    ? attendanceMonth.attendances[expandedEmployeeIndex].days[0]
                        .giustificativi.length
                    : null,
                )
              : {}
          }
          onMouseLeave={() =>
            attendanceMonth
              ? onMouseLeaveCell(
                  employeeIndex,
                  -2,
                  null,
                  expandedEmployeeIndex,
                  attendanceMonth.attendances[employeeIndex].days[0]
                    .giustificativi.length,
                )
              : {}
          }
        >
          <div
            className={clsx(
              "absolute",
              "w-0.5",
              "bg-primary",
              "duration-200",
              "ease-in-out",
              isExpanded ? "h-full" : "h-0",
              "bottom-0",
              "-left-px",
            )}
          />
          {employeeAttendance.employee.matricola}
        </td>

        {/* NAME + EXPAND BUTTON */}
        <td
          className={clsx(
            isExpanded ? "bg-primary-50" : "bg-white",
            "sticky",
            "left-14",
            "z-10",
            "min-w-67",
            "p-0",
            "border-b",
            "border-r",
            "border-neutral",
            "shadow-md",
          )}
          id={makeCellId(employeeIndex, -1, null)}
          onMouseEnter={() =>
            attendanceMonth
              ? onMouseEnterCell(
                  employeeIndex,
                  -1,
                  null,
                  expandedEmployeeIndex,
                  expandedEmployeeIndex
                    ? attendanceMonth.attendances[expandedEmployeeIndex].days[0]
                        .giustificativi.length
                    : null,
                )
              : {}
          }
          onMouseLeave={() =>
            attendanceMonth
              ? onMouseLeaveCell(
                  employeeIndex,
                  -1,
                  null,
                  expandedEmployeeIndex,
                  expandedEmployeeIndex
                    ? attendanceMonth?.attendances[expandedEmployeeIndex]
                        .days[0].giustificativi.length
                    : null,
                )
              : {}
          }
        >
          <button
            onClick={() =>
              setExpandedEmployee((prev) =>
                prev &&
                prev.matricola === employeeAttendance.employee.matricola &&
                prev.filiale === employeeAttendance.employee.filiale
                  ? null
                  : employeeAttendance.employee,
              )
            }
            className={clsx(
              "p-2",
              "w-full",
              "h-12",
              "flex",
              "flex-row",
              "justify-start",
              "items-center",
              "gap-2",
              "cursor-pointer",
            )}
          >
            <FontAwesomeIcon
              icon={faChevronRight}
              className={clsx(
                "ease-in-out",
                "duration-200",
                isExpanded ? "rotate-90 text-primary" : "text-font-secondary",
              )}
            />
            <div
              className={clsx(
                isExpanded && "text-primary font-medium",
                "text-left leading-5",
              )}
            >
              {employeeAttendance.employee.name}
            </div>
          </button>
        </td>

        {/* DAYS (X-SCROLLABLE) */}
        {employeeAttendance.days.map((day, dayIndex) => {
          const actualHours = parseHours(day.actualHours);

          const giustificativiSum = day.giustificativi
            .filter((g) => !POSITIVE_GIUSTIFICATIVI.includes(g.type))
            .reduce((sum, g) => sum + parseHours(g.hours), 0);

          const oreBancaOre = day.giustificativi
            .filter((g) => POSITIVE_GIUSTIFICATIVI.includes(g.type))
            .reduce((sum, g) => sum + parseHours(g.hours), 0);

          const editableHours = actualHours - giustificativiSum;

          return (
            <td
              key={`day-${employeeAttendance.employee.matricola}-${dayIndex}`}
              onMouseEnter={() =>
                attendanceMonth
                  ? onMouseEnterCell(
                      employeeIndex,
                      dayIndex,
                      null,
                      expandedEmployeeIndex,
                      expandedEmployeeIndex
                        ? attendanceMonth.attendances[expandedEmployeeIndex]
                            .days[0].giustificativi.length
                        : null,
                    )
                  : {}
              }
              onMouseLeave={() =>
                attendanceMonth
                  ? onMouseLeaveCell(
                      employeeIndex,
                      dayIndex,
                      null,
                      expandedEmployeeIndex,
                      expandedEmployeeIndex
                        ? attendanceMonth.attendances[expandedEmployeeIndex]
                            .days[0].giustificativi.length
                        : null,
                    )
                  : {}
              }
              id={makeCellId(employeeIndex, dayIndex, null)}
              className={clsx(
                isExpanded
                  ? "bg-primary-50 font-medium"
                  : day.isHoliday
                    ? "bg-canvas-secondary"
                    : "bg-white",
                "min-w-14",
                "relative",
                "border-b",
                dayIndex !== employeeAttendance.days.length - 1 && "border-r",
                "border-neutral",
              )}
            >
              <div className="absolute right-1 bottom-0 text-xs text-font-secondary">
                {formatHours(parseHours(day.theoreticalHours))}
              </div>

              {oreBancaOre !== 0 && (
                <div className="absolute bottom-0 left-1 text-xs text-green-500">
                  +{formatHours(oreBancaOre)}
                </div>
              )}

              {/* Little red dot when something changed */}
              {daysChanged[dayIndex] && (
                <div
                  className={clsx(
                    "absolute",
                    "right-0.5",
                    "top-0.5",
                    "text-xs",
                    "text-font-secondary",
                    "size-1.5",
                    "rounded-full",
                    "bg-primary",
                  )}
                />
              )}

              <div className="text-center w-14">
                <input
                  disabled={
                    isSaving ||
                    isSubmitted === undefined ||
                    isSubmitted ||
                    giustificativiSum !== 0
                  }
                  onFocus={(event) => {
                    setCurrentEditingValue(event.target.value);
                    setFocusedDay(dayIndex);
                    setCurrentScratchEditingValue(formatHours(editableHours));
                  }}
                  onBlur={onBlurRegularHoursInput}
                  onChange={(event) => {
                    setCurrentScratchEditingValue(event.target.value);
                  }}
                  value={
                    currentScratchEditingValue !== null &&
                    focusedDay === dayIndex
                      ? currentScratchEditingValue
                      : formatHours(editableHours)
                  }
                  className={clsx(
                    "w-10",
                    "outline-none",
                    "rounded-sm",
                    "text-center",
                    isSaving && "text-disabled",
                  )}
                />
              </div>
            </td>
          );
        })}

        {/* SUM */}
        <td
          className={clsx(
            isExpanded && "font-medium",
            "sticky",
            "right-30",
            "z-10",
            "min-w-16",
            "p-2",
            "text-center",
            "border-b",
            "border-l",
            "border-neutral",
            "shadow-md",
            isExpanded ? "bg-primary-50" : "bg-white",
            "duration-200",
            "ease-in-out",
          )}
        >
          {totalOreBancaOre !== 0 && (
            <div className="absolute bottom-0 left-1 text-xs text-green-500">
              +{formatHours(totalOreBancaOre)}
            </div>
          )}
          <div className="absolute right-1 bottom-0 text-xs text-font-secondary">
            {formatHours(parseHours(totalHours))}
          </div>
          {formatHours(parseHours(totalActualHours))}
        </td>

        {/* ACTION */}
        <td
          className={clsx(
            "sticky",
            "right-0",
            "z-10",
            "min-w-30",
            "p-2",
            "items-center",
            "border-b",
            "border-l",
            "border-r",
            "border-neutral",
            hasRoundedBottom && "rounded-br-xl",
            isExpanded ? "bg-primary-50" : "bg-white",
            "duration-200",
            "ease-in-out",
          )}
        >
          <div
            className={clsx(
              "flex",
              "flex-row",
              "justify-center",
              "gap-1",
              "items-center",
            )}
          >
            <EmployeeAttendanceActionButton
              icon={faComment}
              onClick={onNoteOpen}
              active={employeeAttendance.note.trim() !== ""}
            />
            <EmployeeAttendanceActionButton
              icon={faReceipt}
              onClick={onVociOpen}
              active={employeeAttendance.voci.length !== 0}
            />
            {!isSubmitted &&
            (daysChanged.reduce((p, c) => p || c, false) ||
              employeeAttendance.note.trim() !== "" ||
              employeeAttendance.voci.length !== 0) ? (
              <EmployeeAttendanceActionButton
                icon={faArrowTurnLeft}
                onClick={() =>
                  openRestoreEmployeeDialog(employeeAttendance.employee)
                }
                alwaysPrimary
              />
            ) : (
              <div className="size-8" />
            )}
          </div>
        </td>
      </tr>

      {/* GIUSTIFICATIVI ROWS */}
      {/*
       * <tr> cannot be animated directly,
       * which is why we need an inner <div>
       */}
      {isExpanded && (
        <>
          {giustificativi.map((giustificativo, giustificativoIndex) => (
            <AttendanceDesktopGiustificativoRow
              key={`giustif.-row-${
                employeeAttendance.employee.matricola
              }-${employeeAttendance.employee.filiale}-${giustificativo.type}`}
              collapsibleCellClasses={collapsibleCellClasses}
              isExpanded={isExpanded}
              setCurrentEditingValue={setCurrentEditingValue}
              employeeAttendance={employeeAttendance}
              employeeIndex={employeeIndex}
              giustificativo={giustificativo}
              giustificativoIndex={giustificativoIndex}
              isSaving={isSaving}
              onBlurGiustificativoInput={onBlurGiustificativoInput}
              openRemoveGiustificativoConfirmation={() =>
                openRemoveGiustificativoConfirmation(giustificativo)
              }
            />
          ))}
          {giustificativiSearchResults !== undefined && (
            <AttendanceDesktopAddGiustificativoRow
              isExpanded={isExpanded}
              addGiustificativoPopoverOpen={addGiustificativoPopoverOpen}
              setAddGiustificativoPopoverOpen={setAddGiustificativoPopoverOpen}
              collapsibleCellClasses={collapsibleCellClasses}
              employeeAttendance={employeeAttendance}
              employeeIndex={employeeIndex}
              giustificativiOptions={giustificativiSearchResults}
              giustificativoSearchQuery={giustificativoSearchQuery}
              onAddGiustificativo={onAddGiustificativo}
              onChangeGiustificativoSearchQuery={
                onChangeGiustificativoSearchQuery
              }
            />
          )}
          <ConfirmationDialog
            isOpen={removeGiustificativoConfirmationOpen}
            onCancel={closeRemoveGiustificativoConfirmation}
            onConfirm={onRemoveGiustificativo}
            titleString={translate("areYouSure", lang)}
            messageString={translate(
              "youAreAboutToRemoveGiustificativo",
              lang,
              {
                "{giustificativo}": wantsToRemoveGiustificativo
                  ? `${wantsToRemoveGiustificativo.type} - ${
                      showTranslated(wantsToRemoveGiustificativo.description, lang)
                    }`
                  : "",
                "{employeeName}": employeeAttendance.employee.name,
              },
            )}
          />
        </>
      )}
    </>
  );
}
